import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { BillingProfile, StatusRes } from '../types/rest-api';

/**
 * An organisation's invoice details (OpenAPI tag: Billing).
 *
 * Two parallel route families over one backend implementation: a Schulgemeinde
 * bills for its whole tree, a standalone school unit bills for itself. A unit
 * *inside* a Schulgemeinde has no billing of its own and the backend rejects it,
 * so callers must not offer this screen there.
 */
@Injectable({
  providedIn: 'root',
})
export class BillingProfileApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  private url(org: BillingOrganisation): string {
    return org.kind === 'authority'
      ? `${this.baseUrl}/v1/school-authorities/${org.id}/billing-profile`
      : `${this.baseUrl}/v1/schools/${org.id}/billing-profile`;
  }

  getProfile(org: BillingOrganisation): Observable<BillingProfile> {
    return this.http.get<BillingProfile>(this.url(org));
  }

  /**
   * Stores the details and pushes them to Stripe.
   *
   * Takes effect from the NEXT invoice: Stripe cannot re-address invoices that
   * have already been issued, so the UI must not promise more than that.
   */
  updateProfile(
    org: BillingOrganisation,
    profile: BillingProfile
  ): Observable<StatusRes> {
    return this.http.put<StatusRes>(this.url(org), profile);
  }
}

/** Which organisation's invoice details we are reading or editing. */
export type BillingOrganisation = {
  kind: 'authority' | 'school';
  id: string;
};
